import { Webhook } from 'lucide-react';
import type { LBNodeData } from '@/proto/logiblocks/logiblocks';
import { ACTION_ICON_COLOR } from '../../styles';
import {
  type GraphData,
  type LBNodeInfo,
  NodeCategory,
  OutputType,
} from '../../types';
import type { ValidationError } from '../../validation';
import {
  validateRequiredTemplate,
  validateTemplateChunks,
} from '../../validation';
import { CallWebhookActionCustomization } from './CallWebhookActionCustomization';
import { DEFAULT_CALL_WEBHOOK_ACTION } from './defaults';
import { defaultNodeData } from '../../graphUtils';
import { formatTemplateChunks } from '../../variables/variableUtils';

/** Validation field names for the Call Webhook action. Shared with the UI. */
export const CallWebhookActionFields = {
  URL: 'Webhook URL',
  BODY: 'Body',
  HEADERS: 'Headers',
} as const;

export const CallWebhookActionNodeInfo: LBNodeInfo = {
  protobufNodeDataKey: 'call_webhook_action',

  category: NodeCategory.ACTION,

  label: 'Call Webhook',

  hasInput: true,

  getIcon: (size: number) => <Webhook size={size} color={ACTION_ICON_COLOR} />,

  outputType: OutputType.SINGLE,

  getSubtitle: (data: LBNodeData, graph: GraphData) => {
    const webhook = data.call_webhook_action;
    const url = formatTemplateChunks(webhook?.url_chunks ?? [], graph.nodes);
    if (url === '') {
      return '(no URL configured)';
    }
    const method = webhook?.method || 'POST';
    return `${method} ${url}`;
  },

  getValidationErrors: (
    nodeId: string,
    nodeData: LBNodeData,
    graph: GraphData
  ) => {
    const webhook = nodeData.call_webhook_action;
    const errors: ValidationError[] = [];

    const urlRequired = validateRequiredTemplate(
      webhook?.url_chunks ?? [],
      CallWebhookActionFields.URL
    );
    if (urlRequired) {
      errors.push(urlRequired);
    } else {
      errors.push(
        ...validateTemplateChunks(
          webhook?.url_chunks ?? [],
          nodeId,
          graph,
          CallWebhookActionFields.URL
        )
      );
    }

    errors.push(
      ...validateTemplateChunks(
        webhook?.body_chunks ?? [],
        nodeId,
        graph,
        CallWebhookActionFields.BODY
      )
    );

    (webhook?.headers ?? []).forEach((header, index) => {
      if (!header.key) {
        errors.push({
          type: 'REQUIRED_FIELD',
          field: CallWebhookActionFields.HEADERS,
          displayGroupKey: `header:${index}`,
          message: `Header ${index + 1} needs a name.`,
        });
      }
      errors.push(
        ...validateTemplateChunks(
          header.value_chunks,
          nodeId,
          graph,
          `${CallWebhookActionFields.HEADERS} — ${header.key || `header ${index + 1}`}`
        )
      );
    });

    return errors.length > 0 ? errors : undefined;
  },

  CustomizationComponent: CallWebhookActionCustomization,

  createDefaultNodeData: () =>
    defaultNodeData('call_webhook_action', DEFAULT_CALL_WEBHOOK_ACTION),

  showCustomizePanelOnCreation: true,
};
